/*
  One person's full task history (12-tasks idea 10: Team load › click a name). A side panel with:
    · five counters (Open · In progress · Overdue · Blocked · Done)
    · on-time completion of their last 8 weeks (same rule as the team chart: closed on or before the due date)
    · every task they are on — open first (soonest due), then closed (newest first) with when it closed and
      whether it was on time. A row opens the task in the task pane (the board keeps the row's task, so a
      task closed weeks ago, or one the search hides, opens too).
  Data: GET /tasks?assignee_id=<id> (any status, the board's visibility rules). The list always belongs to
  the person in the title: switching people shows a loading state (never the previous person's rows), and an
  answer that arrives late for someone else is dropped.
*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CircleCheck, CircleDot, ListChecks, OctagonAlert, RefreshCw, TriangleAlert } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { StatusPill } from '@/components/ui/status-pill';
import { DueBadge, PriorityFlag, StatusIcon } from './TaskCard';
import { OnTimeChart } from './TeamLoad';
import {
  completedDay,
  daysBetweenIso,
  dueInfo,
  isBlocked,
  isCancelled,
  isClosed,
  isDone,
  isoDate,
  normalizeStatus,
  onTimeWeeks,
  sortByDue,
  taskAssignees,
  taskProjects,
} from './taskModel';

const HISTORY_LIMIT = 2000;
const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** "On time" / "3 d late" / "No due date" for a closed task. */
export const closedNote = (task) => {
  if (isCancelled(task)) return { label: 'Cancelled', tone: 'outline' };
  const closed = completedDay(task);
  const due = isoDate(task.due_date);
  if (!closed) return { label: 'Closed', tone: 'gray' };
  if (!due) return { label: 'No due date', tone: 'gray' };
  const late = daysBetweenIso(due, closed);
  return late > 0 ? { label: `${late} d late`, tone: 'red' } : { label: 'On time', tone: 'green' };
};

function Stat({ label, value, icon: Icon, tone, testId }) {
  return (
    <div className="min-w-0 rounded-xl border border-line bg-surface px-3 py-2.5" data-testid={testId}>
      <div className="flex min-w-0 items-center gap-1.5">
        <Icon className={cn('h-3.5 w-3.5 shrink-0', tone)} aria-hidden="true" />
        <span className="truncate whitespace-nowrap text-[12px] font-semibold text-ink-3">{label}</span>
      </div>
      <div className="mt-1 font-heading text-[22px] font-extrabold leading-none tracking-[-0.02em] text-ink tabular-nums">{value}</div>
    </div>
  );
}

function HistorySkeleton() {
  return (
    <ul className="-mx-1 divide-y divide-[color:var(--line-2)]" aria-hidden="true" data-testid="team-history-loading">
      {[0, 1, 2].map((index) => (
        <li key={index} className="flex items-center gap-3 px-1 py-2.5">
          <span className="h-4 w-4 shrink-0 animate-pulse rounded-full bg-[color:var(--line-2)]" />
          <span className="flex min-w-0 flex-1 flex-col gap-1.5">
            <span className="h-3 animate-pulse rounded bg-[color:var(--line-2)]" style={{ width: `${70 - index * 15}%` }} />
            <span className="h-2.5 w-1/3 animate-pulse rounded bg-[color:var(--line-2)]" />
          </span>
          <span className="h-5 w-14 shrink-0 animate-pulse rounded-full bg-[color:var(--line-2)]" />
        </li>
      ))}
    </ul>
  );
}

function HistoryRow({ task, projectNameById, onOpen }) {
  const projects = taskProjects(task, projectNameById);
  const closed = isClosed(task);
  const note = closed ? closedNote(task) : null;
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(task)}
        className="flex w-full min-w-0 items-center gap-3 px-1 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid={`team-history-task-${task.id}`}
      >
        <StatusIcon status={task.status} />
        <span className="min-w-0 flex-1">
          <span dir="auto" className={cn('block truncate text-start text-[13.5px] font-semibold text-ink', closed && 'text-ink-2')}>{task.name}</span>
          <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-ink-3">
            {projects.length ? <span className="truncate">{projects.map((project) => project.name).join(', ')}</span> : <span>No project</span>}
            {!closed ? <PriorityFlag priority={task.priority} className="text-[12px]" /> : null}
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1 text-[12px]">
          {closed ? (
            <>
              <span className="text-ink-3">
                {isDone(task) && task.completed_at ? `Closed ${formatDate(task.completed_at, { year: 'auto' })}` : normalizeStatus(task.status)}
              </span>
              <StatusPill tone={note.tone} label={note.label} size="sm" hideDot={note.tone === 'gray'} />
            </>
          ) : (
            <DueBadge task={task} />
          )}
        </span>
      </button>
    </li>
  );
}

export default function TeamMemberTaskHistory({
  member,
  open,
  onOpenChange,
  projectNameById = {},
  onOpenTask,
  onShowOnBoard,
}) {
  const memberId = member?.id || '';
  // The loaded list remembers whose it is: while another person's list loads, nothing of the last one shows.
  const [result, setResult] = useState({ memberId: '', tasks: [], error: '' });
  const [refreshing, setRefreshing] = useState(false);
  const requestRef = useRef(0);
  const ready = Boolean(memberId) && result.memberId === memberId; // this person's list (or error) is here
  const tasks = useMemo(() => (ready ? result.tasks : []), [ready, result]);
  const error = ready ? result.error : '';
  const loading = Boolean(memberId) && !ready;

  const load = useCallback(async () => {
    const request = ++requestRef.current; // answers to older requests (another person) are ignored
    if (!memberId) return;
    // Opening the same person again refreshes in place; another person (or Try again) starts empty.
    setResult((current) => (current.memberId === memberId && !current.error ? current : { memberId: '', tasks: [], error: '' }));
    setRefreshing(true);
    try {
      const response = await api.get('/tasks', { params: { page: 1, limit: HISTORY_LIMIT, assignee_id: memberId } });
      if (request !== requestRef.current) return;
      // The server filters by person; checking again keeps the panel right with an older server.
      const items = (response.data?.items || []).filter((task) => taskAssignees(task).some((person) => person.id === memberId));
      setResult({ memberId, tasks: items, error: '' });
    } catch (err) {
      if (request !== requestRef.current) return;
      setResult({ memberId, tasks: [], error: getErrorMessage(err, 'Could not load the history') });
    } finally {
      if (request === requestRef.current) setRefreshing(false);
    }
  }, [memberId]);

  useEffect(() => {
    if (open) load();
    else requestRef.current += 1; // closed: a late answer is not kept
  }, [load, open]);

  const { openTasks, closedTasks, stats, weeks } = useMemo(() => {
    const pending = sortByDue(tasks.filter((task) => !isClosed(task)));
    const closed = tasks
      .filter(isClosed)
      .sort((a, b) => String(b.completed_at || b.updated_at || '').localeCompare(String(a.completed_at || a.updated_at || '')));
    const done = tasks.filter(isDone);
    const dated = done.filter((task) => isoDate(task.due_date) && completedDay(task));
    const onTime = dated.filter((task) => completedDay(task) <= isoDate(task.due_date));
    return {
      openTasks: pending,
      closedTasks: closed,
      stats: {
        open: pending.length,
        inProgress: pending.filter((task) => normalizeStatus(task.status) === 'In Progress').length,
        overdue: pending.filter((task) => !isBlocked(task) && dueInfo(task).kind === 'overdue').length,
        blocked: pending.filter(isBlocked).length,
        done: done.length,
        onTimeRate: dated.length ? Math.round((onTime.length / dated.length) * 100) : null,
        dated: dated.length,
      },
      weeks: onTimeWeeks(done),
    };
  }, [tasks]);

  const openTask = (task) => {
    onOpenChange(false);
    onOpenTask?.(task);
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      title={member?.name || 'Team member'}
      subtitle={`${member?.role || 'Team member'} · Full task history`}
      testId="team-member-history-panel"
      footer={(
        <footer className="flex flex-wrap items-center gap-2 border-t border-line-2 bg-surface px-4 py-3 sm:px-[22px] sm:py-3.5">
          <span className="min-w-0 flex-1 text-[12.5px] text-ink-3">
            {loading ? 'Loading…' : `${tasks.length} ${tasks.length === 1 ? 'task' : 'tasks'} in total`}
          </span>
          {onShowOnBoard && member ? (
            <Button type="button" variant="outline" size="sm" onClick={() => { onOpenChange(false); onShowOnBoard(member); }} data-testid="team-history-show-on-board">
              Show on the board
            </Button>
          ) : null}
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </footer>
      )}
    >
      {error ? (
        <EmptyState
          layout="inline"
          tone="red"
          icon={RefreshCw}
          title="Couldn’t load the history"
          text={error}
          titleAs="h3"
          action={<Button size="sm" onClick={load}>Try again</Button>}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-5" aria-busy={loading || refreshing || undefined}>
            <Stat label="Open" value={loading ? '…' : stats.open} icon={ListChecks} tone="text-ink-3" testId="team-history-stat-total" />
            <Stat label="In progress" value={loading ? '…' : stats.inProgress} icon={CircleDot} tone="text-status-blue" testId="team-history-stat-in-progress" />
            <Stat label="Overdue" value={loading ? '…' : stats.overdue} icon={TriangleAlert} tone="text-status-red" testId="team-history-stat-overdue" />
            <Stat label="Blocked" value={loading ? '…' : stats.blocked} icon={OctagonAlert} tone="text-status-red" testId="team-history-stat-blocked" />
            <Stat label="Done" value={loading ? '…' : stats.done} icon={CircleCheck} tone="text-status-navy" testId="team-history-stat-completed" />
          </div>

          <SidePanelSection
            title="On-time completion · last 8 weeks"
            description={loading
              ? 'Closed on or before the due date.'
              : stats.onTimeRate === null
                ? `Closed on or before the due date — nothing with a due date closed yet${stats.done ? ` (${plural(stats.done, 'task')} closed without one)` : ''}.`
                : `Closed on or before the due date — ${stats.onTimeRate}% of ${stats.dated} dated ${stats.dated === 1 ? 'task' : 'tasks'} overall`
                  + `${stats.done > stats.dated ? ` · ${stats.done - stats.dated} more closed without a due date` : ''}.`}
          >
            <OnTimeChart weeks={weeks} compact loading={loading} label={`${member?.name || 'Person'}: on-time completion per week`} />
          </SidePanelSection>

          <SidePanelSection title={loading ? 'Open' : `Open · ${openTasks.length}`}>
            {loading ? <HistorySkeleton /> : openTasks.length ? (
              <ul className="-mx-1 divide-y divide-[color:var(--line-2)]" aria-label="Open tasks">
                {openTasks.map((task) => <HistoryRow key={task.id} task={task} projectNameById={projectNameById} onOpen={openTask} />)}
              </ul>
            ) : (
              <p className="text-[13px] text-ink-3">Nothing open right now.</p>
            )}
          </SidePanelSection>

          {/* keyed: once loaded it opens or folds by the real count (12 or fewer = open) */}
          <SidePanelSection
            key={`${memberId}:${ready ? 'ready' : 'loading'}`}
            title={loading ? 'Closed' : `Closed · ${closedTasks.length}`}
            collapsible
            defaultOpen={closedTasks.length <= 12}
          >
            {loading ? <HistorySkeleton /> : closedTasks.length ? (
              <ul className="-mx-1 divide-y divide-[color:var(--line-2)]" aria-label="Closed tasks">
                {closedTasks.map((task) => <HistoryRow key={task.id} task={task} projectNameById={projectNameById} onOpen={openTask} />)}
              </ul>
            ) : (
              <p className="text-[13px] text-ink-3">No closed tasks yet.</p>
            )}
          </SidePanelSection>
        </>
      )}
    </SidePanel>
  );
}
